import React from 'react'
import Head from 'next/head'
import dynamic from 'next/dynamic'
import { withRouter } from 'next/router'
const EmptySearch = dynamic(() =>
  import('../../components/Error/EmptySearchResult')
)
import {
  mapFiltersFromUrl,
  generateQueryString,
  convertFilters,
} from '../../lib/filterFormatter'
import { get, headers } from '../../lib/fetch'
import { cookieValuetoJSON } from '../../lib/objectValidation'
import { getLayoutByName } from '../../components/LoadLayout'
import LayoutWithFilterAndSort from '../../components/LayoutWithFilterAndSort'
import { GTM_ENV_VAR, PAGE_TYPES } from '../../lib/eventTrackerFunction'
import { getActiveExperimentVariants } from '../../lib/getActiveExperimentVariants'
import { getIosSmartBannerContent } from '../../utils/getIosSmartBannerContent'
import { getUserType } from '../../utils/userType'
import {
  AMP_EVENT_NAME as AEN,
  CTA_LOCATION_NAME,
  getPageTypeByUrl,
} from '../../lib/amplitude'
import { setCtaLocationMeta } from '../../lib/utils'
import { EventTrackingConsumer } from '../../components/EventTrackingProvider/EventTrackingProvider'
import _get from 'lodash/get'
import { getConfigFor } from '../../utils/configService'
import { SPLIT_FEATURES } from '../../utils/constants'
import {
  initDyCookies,
  initDyParams,
  setDySessionFromLayout,
} from '../../utils/DyUtils'
const NewEmptySearchResult = dynamic(() =>
  import('../../components/Error/NewEmptySearchResult')
)
import getCanonicalUrl from '../../lib/getCanonicalUrl'

const PRODUCT_LAYOUT_NAME = 'ProductCollection'

const getQueryData = async (
  checkoutAddress,
  router,
  userkey,
  activeExperiments = ''
) => {
  const dyParams = initDyParams(router)
  const api = `layout/search/v2`
  const { query } = router || {}
  const queryStringObject = {
    q: encodeURIComponent(query.query),
    orderType: getUserType(router) === 'B2B' ? 'B2B' : 'DELIVERY',
  }
  if (activeExperiments.length) {
    const { configuration } = getConfigFor(
      SPLIT_FEATURES.ALGOLIA_PERSONALISATION
    )
    const { personalisationSettings } = configuration
    queryStringObject.algopers = personalisationSettings
    queryStringObject.experiments = activeExperiments
  }
  if (checkoutAddress && checkoutAddress.storeId) {
    const storeId = checkoutAddress.storeId
    queryStringObject.storeId = storeId
  }

  if (query.filter) {
    const transferredFilter = mapFiltersFromUrl(query.filter)
    queryStringObject.filter = convertFilters(transferredFilter)
  }
  if (query.sorting) {
    queryStringObject.sorting = query.sorting
  }

  queryStringObject.includeTagDetails = true

  const requestUrl = api + generateQueryString(queryStringObject) + dyParams
  const response = await get(requestUrl, {
    headers: {
      ...headers(router),
      'x-device-Id': userkey,
    },
  })
  return response
}

class SearchPage extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      isStoreChanged: false,
    }
  }

  triggerSearchViwedEvent() {
    const { isStoreChanged } = this.state
    const data = isStoreChanged
      ? this.state.storeSpecificProduct
      : this.props.data
    const layouts = data?.data?.page?.layouts || []
    const productLayout =
      layouts && getLayoutByName(layouts, PRODUCT_LAYOUT_NAME)
    const count = productLayout?.value?.collection?.count || 0
    setCtaLocationMeta([this.props.router.query.query])
    const GEV = GTM_ENV_VAR
    this.props.track({
      [GEV.AMPLITUDE_EVENT_NAME]: AEN.SEARCH_RESULT_PAGE_VIEWED,
      [GEV.AMPLITUDE_SEARCH_TERM_SELECTED]: this.props.router.query.query,
      [GEV.AMPLITUDE_CTA_LOCATION]: {
        pageName: getPageTypeByUrl(),
        pageMeta: [this.props.router.query.query] || [],
      },
      // common prop
      // GA
      [GEV.SEARCH_RES_COUNT]: count,
      // Amplitude
      [GEV.TOTAL_PRODUCT_COUNT]: count,
      // GA
      [GEV.EVENT_CAT]: 'product-core',
      [GEV.EVENT_ACTION]: 'searchlanded',
      [GEV.EVENT_LABEL]: JSON.stringify({
        searchresult: this.props.router.query.query,
      }),
      [GEV.SCREEN_NAME]: 'Search Result',
      [GEV.PAGE_TYPE]: 'Search',
      [GEV.KEYWORD_SELECTED]: this.props.router.query.query,
      [GEV.ASSET_TYPE]: 'FPON',
    })
  }

  componentDidMount() {
    setDySessionFromLayout(this.props.data?.data?.page?.layouts)
    setCtaLocationMeta([this.props.router.query.query])
    this.triggerSearchViwedEvent()
    initDyCookies()
  }

  async componentDidUpdate(prevProps) {
    if (
      prevProps.checkoutAddress &&
      String(prevProps.checkoutAddress.storeId) !==
        String(this.props.checkoutAddress.storeId)
    ) {
      const { router } = this.props
      const query = router?.query || {}
      if (Object.keys(query).length && query?.query) {
        const activeExperiments = getActiveExperimentVariants()
        const response = await getQueryData(
          this.props.checkoutAddress,
          router,
          this.props?.userkey,
          activeExperiments
        )
        this.setState({
          storeSpecificProduct: { data: response.data },
          isStoreChanged: true,
        })
        setDySessionFromLayout(response.data?.page?.layouts)
      } else {
        this.setState({
          storeSpecificProduct: { data: {}, isStoreChanged: true },
        })
      }
    }
    // check for query changes to use changed data from props instead of state
    if (
      (prevProps.router.query.query !== this.props.router.query.query ||
        prevProps.router.query.filter !== this.props.router.query.filter ||
        prevProps.router.query.sorting !== this.props.router.query.sorting) &&
      this.state.isStoreChanged
    ) {
      this.setState({
        isStoreChanged: false,
      })
    }
    // check for query changes to trigger AMP event
    /* istanbul ignore if  */
    if (prevProps.router.query.query !== this.props.router.query.query) {
      this.triggerSearchViwedEvent()
    }
  }

  render() {
    const {
      organizationData,
      router,
      categoryList,
      iosSmartBannerContent,
      checkoutAddress,
    } = this.props
    const { isStoreChanged } = this.state
    const data = isStoreChanged
      ? this.state.storeSpecificProduct
      : this.props.data
    const layouts = data?.data?.page?.layouts || []
    const productLayout =
      layouts && getLayoutByName(layouts, PRODUCT_LAYOUT_NAME)

    const isProductsAvailable =
      productLayout?.value?.collection?.product &&
      productLayout?.value?.collection?.product.length

    let count

    const { isEnabled: isNoResultEnabled } = getConfigFor(
      SPLIT_FEATURES.FIX_EMPTY_SEARCHPAGE
    )

    if (isNoResultEnabled) {
      count =
        (productLayout?.value?.collection?.count && isProductsAvailable) || 0
    } else {
      count = productLayout?.value?.collection?.count || 0
    }

    const { isEnabled: isZeroSearchResultEnabled } = getConfigFor(
      SPLIT_FEATURES.FE_ZERO_SEARCH_SWIMLANE_ENABLED
    )

    return count ? (
      <React.Fragment>
        <Head>
          <title>Results For {router.query.query} | FairPrice</title>
          <meta
            name="description"
            key="meta_description"
            content={`Shop the best deals for ${router.query.query} with a wide range of products with prices matched online and in stores.`}
          />
          <meta name="apple-itunes-app" content={iosSmartBannerContent} />
          <link rel="canonical" href={getCanonicalUrl(router.asPath)} />
        </Head>
        <LayoutWithFilterAndSort
          title={`Results for "${router.query.query}"`}
          layouts={layouts}
          preLoadFirstImage={true}
          organizationData={organizationData}
          categoryList={categoryList}
          ctaLocation={{
            pageName: CTA_LOCATION_NAME.SEARCH,
            pageMeta: [router.query.query],
          }}
          config={{
            pageType: PAGE_TYPES.SEARCH,
            url: router.query.query,
            loc: `ProductListing-${PAGE_TYPES.SEARCH}`,
          }}
          filterExcept={['PWP', 'isEndlessAisle']}
        />
      </React.Fragment>
    ) : isZeroSearchResultEnabled ? (
      <NewEmptySearchResult
        organizationData={organizationData}
        categoryList={categoryList}
        storeId={checkoutAddress.storeId}
      />
    ) : (
      <EmptySearch
        organizationData={organizationData}
        categoryList={categoryList}
      />
    )
  }
}
const SearchPageWrapper = props => (
  <EventTrackingConsumer>
    {({ track }) => <SearchPage {...props} track={track} />}
  </EventTrackingConsumer>
)
SearchPageWrapper.defaultProps = { data: {} }

SearchPageWrapper.getInitialProps = async ctx => {
  const { query, res } = ctx
  /* istanbul ignore next */
  typeof window === 'undefined' &&
    res.startTime('beforesearchapi', 'before search api')
  const userkey = ctx.res
    ? _get(ctx, 'res.locals.userKey')
    : window.__NEXT_DATA__.props.userKey
  const user_type = getUserType(ctx)
  const iosSmartBannerContent = getIosSmartBannerContent(ctx)
  const checkoutAddress = cookieValuetoJSON(
    ctx,
    user_type === 'B2B' ? 'b2bCheckoutAddress' : 'checkoutAddress'
  )
  const activeExperiments = getActiveExperimentVariants() || ctx.experiments
  /* istanbul ignore next */
  typeof window === 'undefined' && res.endTime('beforesearchapi')
  typeof window === 'undefined' && res.startTime('searchapi', 'search api')
  if (Object.keys(query).length && query.query) {
    const response = await getQueryData(
      checkoutAddress,
      ctx,
      userkey,
      activeExperiments
    )
    /* istanbul ignore next */
    typeof window === 'undefined' && res.endTime('searchapi')
    return {
      data: { data: response.data },
      query,
      iosSmartBannerContent,
      userkey,
    }
  } else {
    /* istanbul ignore next */
    typeof window === 'undefined' && res.endTime('searchapi')
    return { data: {}, query, iosSmartBannerContent }
  }
}

export default withRouter(SearchPageWrapper)
